import { bindable, BindingEngine, bindingMode, autoinject } from 'aurelia-framework';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { I18N } from 'aurelia-i18n';
import { BasePage } from '../../pages/base-page';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SelectBucket } from '../select-bucket/select-bucket';
import { ValidationController, ValidationRules } from 'aurelia-validation';

let getAccountTypeForShort = function(accountType) {
	if (!accountType) { return ''; }
	if (accountType.value == 'General Ledger') { return 'G'; }
	if (accountType.value == 'Loan') { return 'L'; }
	if (accountType.value == 'Share Savings') { return 'S'; }
	if (accountType.value == 'Share Checking') { return 'C'; }
	if (accountType.value == 'Share') { return 'S'; }
	if (accountType.value == 'External Loan') { return 'EL'; }
	return;
};

@bindable(
	{
		name: 'accountNumber',
		defaultBindingMode: bindingMode.twoWay
	}
)
@bindable(
	{
		name: 'suffixVisible',
		defaultBindingMode: bindingMode.oneWay
	}
)
@bindable(
	{
		name: 'suffixRequired',
		defaultBindingMode: bindingMode.oneWay
	}
)
@bindable(
	{
		name: 'hideLabels',
		defaultBindingMode: bindingMode.oneWay
	}
)
@bindable(
	{
		name: 'considerAccountType',
		defaultBindingMode: bindingMode.oneWay
	}
)
@bindable(
	{
		name: 'considerMethod',
		defaultBindingMode: bindingMode.oneWay
	}
)
@bindable(
	{
		name: 'accountNumberSettings',
		defaultBindingMode: bindingMode.oneWay
	}
)
@bindable(
	{
		name: 'bucket',
		defaultBindingMode: bindingMode.twoWay
	}
)
@bindable(
	{
		name: 'alwaysApplyQueryStringParams',
		defaultBindingMode: bindingMode.oneWay
	}
)
@autoinject
export class CustomerAccountNumberCustomElement extends BasePage {
	globalState: State;
	bindingEngine: BindingEngine;
	payrazrService: PayrazrService;
	disableField: any;
	additionalField: any;
	accountTypeArrayisNotEmpty: any;
	accountTypeArrayIsNotEmpty: any;
	accountTypeFor3rdParty: any;
	disableAccountTypeSelection: any;
	isAccountTypeVisible: any;
	isAccountTypeRequired: any;
	needCustomizableValidation: any;
	accountNumberPiece0: string;
	accountNumberPiece1: any;
	method: any;
	considerMethod: any;
	showAccountTypeFor3rdParty: any;
	considerAccountType: any;
	accountNumberSettings: any;
	piece0Subscription: any;
	piece1Subscription: any;
	piece2Subscription: any;
	piece3Subscription: any;
	accountNumber: any;
	suffixVisible: any;
	suffixRequired: any;
	accountNumberText: string;
	selectBucketComponent: SelectBucket;

	@bindable validationController: ValidationController;

	@bindable isBucketsRenderable: boolean = true;

	@bindable alwaysApplyQueryStringParams: boolean = false;

	@bindable showConsumerLocationDisplayName: boolean = false;

	// We still need default ValidationSettings for security consideration
	fieldValidationSettings = {
		'accountNumber': /^[a-zA-Z0-9\s\.\-\_]+?$/,
		'registrationId': /^[a-zA-Z0-9\.\-\_]+?$/,
		'memberNumber': /^[0-9]+?$/,
		'suffix': /^[a-zA-Z0-9\.]+?$/,
		'micr': /^[a-zA-Z0-9\.]+?$/,
		'additionalField': /^[a-zA-Z0-9\.]+?$/
	};
	constructor(state: State, bindingEngine: BindingEngine, payrazrService: PayrazrService, private i18n: I18N, eventAggregator: EventAggregator) {
		super(eventAggregator);
		this.globalState = state;
		this.bindingEngine = bindingEngine;
		this.payrazrService = payrazrService;
		this.disableField = false;
		this.additionalField = '';
		this.accountTypeArrayIsNotEmpty = this.globalState.business.portalSettings.ezpay.accountTypeFor3rdParty && this.globalState.business.portalSettings.ezpay.accountTypeFor3rdParty.length !== 0;

		if (this.accountTypeArrayIsNotEmpty) {
			this.accountTypeFor3rdParty = this.globalState.business.portalSettings.ezpay.accountTypeFor3rdParty[0];
		}

		this.disableAccountTypeSelection = false;

		this.isAccountTypeVisible = this.globalState.business.portalSettings.ezpay.isAccountTypeVisible || this.globalState.business.fetchCanonicalPortfolioSettings().settings.isAccountTypeVisible;
		this.isAccountTypeRequired = this.globalState.business.portalSettings.ezpay.isAccountTypeRequired || this.globalState.business.fetchCanonicalPortfolioSettings().settings.isAccountTypeRequired;
		this.needCustomizableValidation = this.globalState.business.portalSettings.ezpay.accountNumberHandling.toLowerCase() === 'lookup' ? false : true;
	}

	configureValidationRules() {
		let accountNumberDisplayName = this.getAccountNumberDisplayName();
		let accountNumberPiece1DisplayName = this.getAccountNumberPiece1DisplayName();
		let accountTypeFor3rdPartyDisplayName = this.i18n.i18next.t('accountType', 'Account Type');
		let additionalFieldDisplayName = this.accountTypeFor3rdParty && this.accountTypeFor3rdParty.customFieldLabel ?
			this.accountTypeFor3rdParty.customFieldLabel :
			'';

		ValidationRules
			.ensure('accountNumberPiece0').displayName(accountNumberDisplayName)
			.required()

			.ensure('accountNumberPiece1').displayName(accountNumberPiece1DisplayName)
			.required()

			.ensure('accountTypeFor3rdParty').displayName(accountTypeFor3rdPartyDisplayName)
			.required()
			.when(() => this.isAccountTypeRequired)

			.ensure('additionalField').displayName(additionalFieldDisplayName)
			.required()
			.when(() => this.accountTypeFor3rdParty && this.accountTypeFor3rdParty.customFieldLabel)

			.on(this);
	}

	getAccountNumberDisplayName() {
		let displayName: string = this.accountNumberSettings[0].label ?
			this.accountNumberSettings[0].label : 
			'';

		if (displayName.length < 1) {
			if (this.method === 'micr') {
				displayName = this.i18n.i18next.t('customerAccountNumber', 'Member Number');
			} else if (this.method === 'segment') {
				if (this.accountNumberSettings.length === 1) {
					displayName = this.accountNumberText;
				} else {
					displayName = this.i18n.i18next.t('customerAccountNumber', 'Member Number');
				}
			}
		}

		return displayName;
	}

	getAccountNumberPiece1DisplayName() {
		if (this.accountNumberSettings.length < 2) {
			return '';
		}

		let displayName: string;

		if (this.method === 'micr') {
			displayName = this.accountNumberSettings[1].micrlabel ?
				this.accountNumberSettings[1].micrlabel :
				'MICR';
		} else if (this.method === 'segment') {
			displayName = this.accountNumberSettings[1].label ?
				this.accountNumberSettings[1].label :
				this.i18n.i18next.t('suffix', 'Suffix');
		}
	}

	applyQueryStringParams(params) {
		if (!params) {
			return;
		}

		try {

			if (params.getParameterValue('locked')) {
				this.disableField = true;
			}

			if (params.getParameterValue('UMcustID')) {
				this.accountNumberPiece0 = params.getParameterValue("UMcustID").substr(0, 35);
			}

			if (params.getParameterValue('accountNumber')) {
				this.accountNumberPiece0 = params.getParameterValue("accountNumber").substr(0, 35);
			}

			if (params.getParameterValue('memberNumber')) {
				this.accountNumberPiece0 = params.getParameterValue("memberNumber").substr(0, 35);
			}

			if (params.getParameterValue('suffix') || params.getParameterValue('micr')) {
				this.accountNumberPiece1 = params.getParameterValue("suffix") ? params.getParameterValue("suffix").substr(0, 35) : params.getParameterValue("micr").substr(0, 35);
			}

			let accountTypes = !!this.globalState.business.portalSettings.ezpay.accountTypeFor3rdParty ? this.globalState.business.portalSettings.ezpay.accountTypeFor3rdParty : [];

			if (params.getParameterValue('accountType') && accountTypes.length > 0) {
				let selectedAccountType = accountTypes.find(object => object.description === params.getParameterValue('accountType'));

				if (!selectedAccountType) {
					selectedAccountType = accountTypes.find(object => object.value === params.getParameterValue('accountType'));
				}

				if (selectedAccountType) {
					this.accountTypeFor3rdParty = selectedAccountType;
					this.disableAccountTypeSelection = this.disableField;

					if (this.accountTypeFor3rdParty.customFieldLabel && params.getParameterValue('loanId')) {
						this.additionalField = params.getParameterValue('loanId');
					}
				}
			}
		}
		catch (err) {
			console.log('Error encountered in querystring params.');
		}
	}

	loadTranslations() {
		this.accountNumberText = this.i18n.i18next.t('accountNumber', 'Account Number');
	}

	attached() {
		super.attached();
		this.method = 'segment';
		if (this.considerMethod === 'true' && !!this.globalState.business.portalSettings.ezpay.accountNumberMethod) {
			this.method = this.globalState.business.portalSettings.ezpay.accountNumberMethod;
		}

		let hasAccountTypeFor3rdParty = false;
		let ezpayPortfolio = this.globalState.business.fetchPortfolioSettings(this.globalState.business.ezpayPortfolioName);

		if (ezpayPortfolio && this.isAccountTypeVisible) {
			hasAccountTypeFor3rdParty = true;
		}

		if (hasAccountTypeFor3rdParty === false) {
			let portfolios = this.globalState.business.portfolioSettings;
			if (portfolios) {
				let index = 0;

				for (let portfolioName in portfolios) {
					let portfolio = this.globalState.business.fetchPortfolioSettings(portfolioName);
					if (portfolio && portfolio.merchantSettings && portfolio.merchantSettings.hasAccountTypeFor3rdParty) {
						hasAccountTypeFor3rdParty = true;
						break;
					}
				}
			}
		}

		this.showAccountTypeFor3rdParty = this.considerAccountType === 'true'
			&& hasAccountTypeFor3rdParty
			&& this.method === 'segment'
			&& this.accountTypeArrayIsNotEmpty
			&& this.isAccountTypeVisible;

		if (this.alwaysApplyQueryStringParams) {
			this.applyQueryStringParams(this.payrazrService);
		}

		this.loadTranslations();

		this.configureValidationRules();
	}

	// This will only ever call the portalsettings
	validate(errors) {
		let settings = this.accountNumberSettings;
		if (this.accountNumberSettings.length === 1) {
			// I think we still need verify user's input for registrationId by a flexible default validationExpression for security consideration
			// But I do not change the current behavior since this is not in planned scope. Just make a comment it here. Easy change to be verified for registrationId now.
			const defaultLabel = this.i18n.i18next.t('accountNumber','Account Number');
			const fieldName = 'accountNumber';
			if (settings[0].validationExpression && this.needCustomizableValidation) { this.customizableValidation(this.accountNumberPiece0, settings[0].label || defaultLabel, settings[0].validationExpression, true, errors, settings[0].validationMessage); }
			else { this.fieldValidation(this.accountNumberPiece0, settings[0].label || defaultLabel, this.fieldValidationSettings[fieldName], true, errors, fieldName); }
		} else {
			if (settings[0].validationExpression && this.needCustomizableValidation) { this.customizableValidation(this.accountNumberPiece0, settings[0].label || "member number", settings[0].validationExpression, true, errors, settings[0].validationMessage); }
			else { this.fieldValidation(this.accountNumberPiece0, settings[0].label || "Member Number", this.fieldValidationSettings["memberNumber"], true, errors, "memberNumber"); }

			const defaultSegments1Label = this.method === 'micr' ? 'MICR' : 'Suffix';
			const fieldName = this.method === 'micr' ? 'micr' : 'suffix';
			if (settings[1].validationExpression && this.needCustomizableValidation) { this.customizableValidation(this.accountNumberPiece1, settings[1].label || defaultSegments1Label, settings[1].validationExpression, this.suffixRequired == null ? true : this.suffixRequired, errors, settings[1].validationMessage); }
			else { this.fieldValidation(this.accountNumberPiece1, settings[1].label || defaultSegments1Label, this.fieldValidationSettings[fieldName], this.suffixRequired == null ? true : this.suffixRequired, errors, fieldName); }

			if (this.showAccountTypeFor3rdParty) {
				const fieldName = 'additionalField';
				if (!!this.accountTypeFor3rdParty.customFieldLabel) {
					this.fieldValidation(this.additionalField, this.accountTypeFor3rdParty.customFieldLabel, this.fieldValidationSettings[fieldName], true, errors, fieldName);
				}
			}
		}

		if (this.selectBucketComponent)
			this.selectBucketComponent.validate(errors);
	}

	bind() {
		
		this.piece0Subscription = this.bindingEngine.propertyObserver(this, 'accountNumberPiece0')
			.subscribe((newValue, oldValue) => {
				if (this.method === 'micr') {
					if (!!this.accountNumberPiece1 && (this.accountNumberPiece1.match(/^[12]\d{12}?$/) != null)) {
						this.accountNumber = `${this.accountNumberPiece1.substr(3, 10)}-${this.accountNumberPiece1.substr(1, 2)}${this.accountNumberPiece1.substr(0, 1) === '1' ? 'S' : 'L'}`;
					}
					else {
						this.accountNumber = this.accountNumberPiece1;
					}
				}
				else {
					let accountNumberPiece1 = '';
					if (!!this.accountNumberPiece1) {
						accountNumberPiece1 = this.accountNumberPiece1;
					}

					this.accountNumber = ((this.accountNumberSettings.length === 1) || (!this.accountNumberPiece0)) || (!this.suffixVisible && !this.showAccountTypeFor3rdParty)
						? newValue
						: `${newValue}-${accountNumberPiece1}${this.additionalField}${this.showAccountTypeFor3rdParty ? getAccountTypeForShort(this.accountTypeFor3rdParty) : ''}`;
				}
			});
		this.piece1Subscription = this.bindingEngine.propertyObserver(this, 'accountNumberPiece1')
			.subscribe((newValue, oldValue) => {
				if (this.method === 'micr') {
					if (!!newValue && (newValue.match(/^[12]\d{12}?$/) != null)) {
						this.accountNumber = `${newValue.substr(3, 10)}-${newValue.substr(1, 2)}${newValue.substr(0, 1) === '1' ? 'S' : 'L'}`;
					}
					else {
						this.accountNumber = newValue;
					}
				}
				else {
					this.accountNumber = ((!this.accountNumberPiece0) || (!newValue))
						? this.accountNumberPiece0
						: `${this.accountNumberPiece0}-${newValue}${this.additionalField}${this.showAccountTypeFor3rdParty ? getAccountTypeForShort(this.accountTypeFor3rdParty) : ''}`;
				}
			});
		this.piece2Subscription = this.bindingEngine.propertyObserver(this, 'accountTypeFor3rdParty')
			.subscribe((newValue, oldValue) => {
				this.additionalField = '';
				this.accountNumber = `${this.accountNumberPiece0}-${this.accountNumberPiece1}${this.additionalField}${getAccountTypeForShort(newValue)}`;
			});
		this.piece3Subscription = this.bindingEngine.propertyObserver(this, 'additionalField')
			.subscribe((newValue, oldValue) => {
				let _accountNumberPiece1 = this.accountNumberPiece1 ? this.accountNumberPiece1 : '';

				this.accountNumber = `${this.accountNumberPiece0}-${_accountNumberPiece1}${newValue}${getAccountTypeForShort(this.accountTypeFor3rdParty)}`;
			});
	}

	unbind() {
		this.piece0Subscription.dispose();
		this.piece1Subscription.dispose();
		this.piece2Subscription.dispose();
		this.piece3Subscription.dispose();
	}

	// This validation is set through the portal settings.
	customizableValidation(piece, label, validationExpression, isRequired, errors, validationMessage) {
		if (validationExpression && piece.length > 0) {
			if (piece.match(validationExpression) == null) {
				if (validationMessage) {
					errors.push(validationMessage);
					return;
				}
				errors.push(this.i18n.i18next.t('labelNotFormattedCorrectly', { defaultValue: `The ${label} entered is not formatted correctly.`, label }));
			}
		} else if (piece.length === 0 && isRequired) {
			errors.push(this.i18n.i18next.t('pleaseEnterYour', { defaultValue: `Please enter your ${label}.`, label }));
		}
	}

	// Business logic for the member number, suffix, and account number/registrationId
	fieldValidation(piece, label, validationExpression, isRequired, errors, fieldName) {
		if (isRequired && !piece) {
			errors.push(this.i18n.i18next.t('pleaseEnterYour', { defaultValue: `Please enter your ${label}.`, label }));
			return;
		}

		if (!validationExpression) {
			return;
		}

		if (piece && piece.match(validationExpression) == null) {
			if (fieldName === 'memberNumber') {
				errors.push(this.i18n.i18next.t('labelNotFormattedCorrectlyMustBeNumberBetweenOneAndTenDigits' , { defaultValue: `The ${label} entered is not formatted correctly: must be a number between 1 and 10 digits.`, label: label }));
			}
			else if (fieldName === 'accountNumber') {
				errors.push(this.i18n.i18next.t('labelNotFormattedCorrectlyOnlyValidIs', { defaultValue: `The ${label} entered is not formatted correctly: (A-Z), (0-9), period, and hyphen are valid.`, label: label }));
								}
			else if (fieldName === 'registrationId') {
				errors.push(this.i18n.i18next.t('labelNotFormattedCorrectlyOnlyValidIs', { defaultValue: `The ${label} entered is not formatted correctly: (A-Z), (0-9), period, and hyphen are valid.`, label: label }));
								}
			else if (fieldName === 'suffix') {
				errors.push(this.i18n.i18next.t('labelNotFormattedCorrectlySpecialCharactersNotAllowed', { defaultValue: `The ${label} entered is not formatted correctly: special characters not allowed.`, label: label }));
								}
			else if (fieldName === 'micr') {
				errors.push(this.i18n.i18next.t('labelNotFormattedCorrectlySpecialCharactersNotAllowed', { defaultValue: `The ${label} entered is not formatted correctly: special characters not allowed.`, label: label }));
								}
			else {
				errors.push(this.i18n.i18next.t('labelNotFormattedCorrectlyOnlyValidIs', { defaultValue: `The ${label} entered is not formatted correctly: (A-Z), (0-9), period, and hyphen are valid.`, label: label }));
								}
		}
	}
}
